import multiSelectTemplate from './multi-select.html?raw';

import '../dropdown-content/dropdown-content.js';
import '../data-list/data-list.js';
import '../pill/pill.js';

export class MultiSelect extends HTMLElement {
    static get observedAttributes() {
        return [
            'id',
            'name',
            'label',
            'placeholder',
            'search-placeholder',
            'max-displayed',
            'disabled',
            'required',
            'aria-label',
            'aria-describedby',
            'filter-text',
        ];
    }

    constructor() {
        super();

        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);

        this.settings = {
            labelText: 'Select options',
            labelProperty: 'label',
            maxDisplayed: undefined,
            options: [],
            valueProperty: 'value',
            localization: {
                announcementOptionsSelected: null,
                announcementOptionRemoved: null,
                announcementOptionFiltered: null,
                announcementOptionSelected: null,
                andCountMore: null,
                noOptions: null,
                pillLabelText: null,
                dropdown: {
                    openMessage: null,
                    closeMessage: null,
                },
            },
        };

        this.filteredOptions = [];
        this.selectedOptions = [];

        this.dropdownContent = null;
    }

    connectedCallback() {
        const id = `multi-select-${Math.random().toString(36).substring(2, 9)}`;
        this.elementId = this.getAttribute('id') || id;

        this.attachShadow({ mode: 'open' });

        const template = document.createElement('template');
        template.innerHTML = multiSelectTemplate;
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        if (this.hasAttribute('no-options-text')) {
            this.settings.localization.noOptions =
                this.getAttribute('no-options-text');
        }

        if (this.hasAttribute('options')) {
            try {
                this.settings.options = JSON.parse(
                    this.getAttribute('options')
                );
                this.filteredOptions = this.settings.options;

                const datalist = this.shadowRoot.querySelector('svea-datalist');

                if (datalist) {
                    datalist.setOptions(this.filteredOptions);
                    datalist.setAttribute('id', `${this.elementId}-datalist`);
                    datalist.setAttribute(
                        'no-options-text',
                        this.settings.localization.noOptions
                    );
                }
            } catch (e) {
                console.error('Invalid JSON in options attribute:', e);
            }
        }

        const input = this.shadowRoot.querySelector('.multi-select-input');
        const inputId = `${this.elementId}-input`;
        if (input) {
            input.id = inputId;
            input.setAttribute('aria-controls', `${this.elementId}-datalist`);
        }

        this.settings.labelText = this.hasAttribute('label')
            ? this.getAttribute('label')
            : 'Select options';
        const label = this.shadowRoot.querySelector('label');

        if (this.isDisabled) {
            const inputContainer = this.shadowRoot.querySelector(
                '.multi-select-input-container'
            );

            if (
                inputContainer &&
                !inputContainer.classList.contains('disabled')
            )
                inputContainer.classList.add('disabled');
        }

        if (label) {
            label.textContent = this.settings.labelText;
            label.setAttribute('for', inputId);
        }

        const dropdownContent = this.shadowRoot.querySelector(
            'svea-dropdown-content'
        );

        const dropDownLocalization = {};

        if (this.settings.localization.dropdown.openMessage) {
            dropDownLocalization.openMessage =
                this.settings.localization.dropdown.openMessage;
        }
        if (this.settings.localization.dropdown.closeMessage) {
            dropDownLocalization.closeMessage =
                this.settings.localization.dropdown.closeMessage;
        }

        if (dropdownContent)
            dropdownContent.setLocalization(dropDownLocalization);

        this.addEventListeners();

        setTimeout(() => {
            this.handleSelectionChange();
        }, 100);
    }

    initialize(settings) {
        this.settings = { ...this.settings, ...settings };

        if (Object.prototype.hasOwnProperty.call(settings, 'options')) {
            try {
                this.filteredOptions = this.settings.options;

                const datalist = this.shadowRoot.querySelector('svea-datalist');

                if (datalist) {
                    datalist.setOptions(this.filteredOptions);
                    datalist.setAttribute(
                        'no-options-text',
                        this.settings.localization.noOptions
                    );
                }
            } catch (e) {
                console.error('Invalid JSON in options attribute:', e);
            }
        }

        if (Object.prototype.hasOwnProperty.call(settings, 'labelText')) {
            const label = this.shadowRoot.querySelector('label');

            if (label) {
                label.textContent = settings.labelText;
            }
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.shadowRoot === null) return;
        if (oldValue === newValue) return;

        const inputContainer = this.shadowRoot.querySelector(
            '.multi-select-input-container'
        );

        switch (name) {
            case 'label':
                this.settings.labelText = newValue;
                if (this.shadowRoot) {
                    const label = this.shadowRoot.querySelector('label');
                    if (label) {
                        label.textContent = newValue;
                    }
                }
                break;
            case 'no-options-text':
                this.settings.localization.noOptions = newValue;
                if (this.shadowRoot) {
                    const dataList =
                        this.shadowRoot.querySelector('svea-datalist');
                    if (dataList) {
                        dataList.setAttribute('no-options-text', newValue);
                    }
                }
                break;
            case 'max-displayed': {
                const max = Number.parseInt(newValue);
                if (isNaN(max) || max < 1) {
                    this.settings.maxDisplayed = undefined;
                } else {
                    this.settings.maxDisplayed = max;
                }
                this.renderSelectedOptions();
                break;
            }
            case 'disabled':
                if (newValue !== 'false' && newValue !== null) {
                    if (
                        inputContainer &&
                        !inputContainer.classList.contains('disabled')
                    ) {
                        inputContainer.classList.add('disabled');
                    }
                } else {
                    if (
                        inputContainer &&
                        inputContainer.classList.contains('disabled')
                    ) {
                        inputContainer.classList.remove('disabled');
                    }
                }
        }
    }

    addEventListeners() {
        const multiselect = this.shadowRoot.querySelector(
            '.multi-select-container'
        );
        const input = this.shadowRoot.querySelector('.multi-select-input');
        const inputContainer = this.shadowRoot.querySelector(
            '.multi-select-input-container'
        );
        const dropdownContent = this.shadowRoot.querySelector(
            'svea-dropdown-content'
        );
        const dataList = this.shadowRoot.querySelector('svea-datalist');

        const dropdownBtn = this.shadowRoot.querySelector('.dropdown-btn');
        if (dropdownBtn) {
            dropdownBtn.addEventListener('click', (e) => {
                e.stopPropagation();

                const dropdownContent = this.shadowRoot.querySelector(
                    'svea-dropdown-content'
                );

                if (dropdownContent) {
                    if (dropdownContent.isOpen()) {
                        this.closeDropdown();
                        input.removeEventListener('focus', this.handleFocus);
                        input.focus();
                        input.addEventListener('focus', this.handleFocus);
                    } else {
                        this.openDropdown();
                        if (input) {
                            input.focus();
                        }
                    }
                }
            });
        }

        input.addEventListener('input', (e) => this.handleInput(e));
        input.addEventListener('focus', this.handleFocus);
        input.addEventListener('blur', () => this.handleBlur());
        input.addEventListener('keydown', (e) => this.handleKeydown(e));

        inputContainer.addEventListener('click', (e) => {
            e.stopPropagation();

            if (
                e.target.closest('svea-pill') ||
                e.target.tagName === 'SVEA-PILL'
            ) {
                return;
            }

            if (inputContainer.classList.contains('disabled')) return;

            if (e.target.classList.contains('dropdown-btn')) {
                if (multiselect.classList.contains('open')) {
                    multiselect.classList.remove('open');
                    this.closeDropdown();
                    return;
                }
            }

            if (!inputContainer.classList.contains('active'))
                inputContainer.classList.add('active');

            input.focus();
        });

        multiselect.addEventListener('focusin', function (event) {
            if (!multiselect.contains(event.relatedTarget)) {
                if (!inputContainer.classList.contains('active'))
                    inputContainer.classList.add('active');
            }
        });

        multiselect.addEventListener('focusout', function (event) {
            if (!multiselect.contains(event.relatedTarget)) {
                if (multiselect.classList.contains('open'))
                    multiselect.classList.remove('open');

                inputContainer.classList.remove('active');
            }
        });

        if (dropdownContent) {
            dropdownContent.addEventListener('mousedown', (e) => {
                e.preventDefault();
            });

            dropdownContent.addEventListener('dropdown:closed', () => {
                const container = this.shadowRoot.querySelector(
                    '.multi-select-container'
                );

                if (container) {
                    container.classList.remove('open');
                }

                if (input) input.setAttribute('aria-expanded', 'false');
            });
        }

        if (dataList) {
            dataList.addEventListener('mousedown', (e) => {
                e.preventDefault();
            });

            dataList.addEventListener('datalist:selection-changed', (e) => {
                this.onSelectionChange(e);
            });

            dataList.shadowRoot.addEventListener('focusin', (e) => {
                const focusedOption = e.target.closest('.option');

                const input = this.shadowRoot.querySelector(
                    '.multi-select-input'
                );

                if (focusedOption && focusedOption.id && input) {
                    input.setAttribute(
                        'aria-activedescendant',
                        focusedOption.id
                    );
                } else if (input) {
                    input.removeAttribute('aria-activedescendant');
                }
            });

            dataList.shadowRoot.addEventListener('focusout', () => {
                const input = this.shadowRoot.querySelector(
                    '.multi-select-input'
                );

                if (input) input.removeAttribute('aria-activedescendant');
            });

            dataList.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    if (input) {
                        input.focus();
                        input.setAttribute('aria-expanded', 'false');
                    }

                    this.dropdownContent.removeAttribute('open');
                    const container = this.shadowRoot.querySelector(
                        '.multi-select-container'
                    );
                    if (container) {
                        container.classList.remove('open');
                    }
                    event.preventDefault();
                }
            });
        }
    }

    disconnectedCallback() {
        const input = this.shadowRoot.querySelector('.multi-select-input');
        if (input) {
            input.removeEventListener('focus', this.handleFocus);
            input.removeEventListener('blur', this.handleBlur);
        }
    }

    get isDisabled() {
        return (
            this.hasAttribute('disabled') &&
            this.getAttribute('disabled') !== 'false'
        );
    }

    handleFocus() {
        const dropdownList = this.shadowRoot.querySelector(
            'svea-dropdown-content'
        );
        const container = this.shadowRoot.querySelector(
            '.multi-select-container'
        );

        const input = this.shadowRoot.querySelector('.multi-select-input');

        if (input) {
            input.setAttribute('aria-expanded', 'true');
        }

        if (dropdownList) {
            dropdownList.setAttribute('open', '');
        }

        if (container) {
            container.classList.add('open');
        }
    }

    handleBlur() {
        setTimeout(() => {
            const activeElement =
                this.shadowRoot.activeElement || document.activeElement;
            const dropdownContent = this.shadowRoot.querySelector(
                'svea-dropdown-content'
            );
            const container = this.shadowRoot.querySelector(
                '.multi-select-container'
            );

            const input = this.shadowRoot.querySelector('.multi-select-input');

            const dropdownBtn = this.shadowRoot.querySelector('.dropdown-btn');

            if (
                dropdownBtn &&
                (activeElement === dropdownBtn ||
                    (activeElement &&
                        activeElement.closest &&
                        activeElement.closest('.dropdown-btn')))
            ) {
                return;
            }

            if (
                dropdownContent &&
                (dropdownContent.contains(activeElement) ||
                    dropdownContent.shadowRoot?.contains(activeElement) ||
                    (activeElement &&
                        activeElement.closest &&
                        activeElement.closest('svea-dropdown-content')))
            ) {
                return;
            }

            if (input) {
                input.removeAttribute('aria-activedescendant');
                input.setAttribute('aria-expanded', 'false');
            }

            if (dropdownContent) dropdownContent.removeAttribute('open');

            if (container) container.classList.remove('open');

            const dataList = this.shadowRoot.querySelector('svea-datalist');

            if (dataList) {
                const focusedOptions =
                    dataList.shadowRoot?.querySelectorAll('.option.focused') ||
                    [];
                focusedOptions.forEach((option) => {
                    option.classList.remove('focused');
                    option.setAttribute('aria-selected', 'false');
                });
            }

            this.updateLiveRegion('');
        }, 50);
    }

    handleInput() {
        const input = this.shadowRoot.querySelector('.multi-select-input');
        const inputContainer = this.shadowRoot.querySelector(
            '.multi-select-input-container'
        );

        if (!input) return;

        const filterText = input.value.toLowerCase().trim();
        const selectedOptions = this.selectedOptions;

        if (filterText === '') this.filteredOptions = this.settings.options;
        else
            this.filteredOptions = this.settings.options.filter((option) => {
                return option.label.toLowerCase().includes(filterText);
            });

        const filteredWithSelectState = this.filteredOptions.map((option) => ({
            ...option,
            selected: selectedOptions.some((sel) => sel.value === option.value),
        }));

        const dataList = this.shadowRoot.querySelector('svea-datalist');

        if (dataList) dataList.setOptions(filteredWithSelectState);

        if (filterText || selectedOptions.length > 0)
            inputContainer.classList.add('has-options');
        else inputContainer.classList.remove('has-options');

        if (input.value && !input.classList.contains('has-text'))
            input.classList.add('has-text');
        else if (!input.value && input.classList.contains('has-text'))
            input.classList.remove('has-text');

        this.setAttribute('filter-text', filterText);

        if (filterText !== '') {
            if (this.settings.localization?.announcementOptionFiltered)
                this.updateLiveRegion(
                    this.settings.localization.announcementOptionFiltered.replace(
                        '{count}',
                        this.filteredOptions.length
                    )
                );
            else
                this.updateLiveRegion(
                    `${this.filteredOptions.length} options available`
                );
        } else this.updateLiveRegion('');
    }

    handleKeydown(event) {
        const input = this.shadowRoot.querySelector('.multi-select-input');

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (this.isDropdownClosed()) {
                    this.openDropdown();
                }
                this.navigateOptions('down');
                break;

            case 'ArrowUp':
                event.preventDefault();
                if (this.isDropdownClosed()) {
                    this.openDropdown();
                }
                this.navigateOptions('up');
                break;

            case 'Enter':
                event.preventDefault();
                this.selectCurrentOption();
                break;

            case 'Escape':
                event.preventDefault();
                this.closeDropdown();
                break;

            case 'Tab':
                if (!event.shiftKey) {
                    event.preventDefault();
                    this.focusNextElement();
                }

                break;

            case 'Backspace':
                if (input && input.value === '') {
                    this.removeLastSelectedOption();
                }
                break;

            case ' ':
                if (this.getCurrentOption()) {
                    event.preventDefault();
                    this.selectCurrentOption();
                }
                break;
            default:
                if (this.isDropdownClosed()) {
                    this.openDropdown();
                }
                break;
        }
    }

    navigateOptions(direction) {
        const dataList = this.shadowRoot.querySelector('svea-datalist');
        if (!dataList) return;

        dataList.navigate(direction);
    }

    findCurrentOptionIndex(options) {
        for (let i = 0; i < options.length; i++) {
            if (
                options[i].classList.contains('focused') ||
                options[i].getAttribute('aria-selected') === 'true'
            ) {
                return i;
            }
        }
        return -1;
    }

    setCurrentOption(option) {
        const dataList = this.shadowRoot.querySelector('svea-datalist');
        if (!dataList) return;

        const allOptions =
            dataList.shadowRoot?.querySelectorAll('.option') || [];
        allOptions.forEach((opt) => {
            opt.classList.remove('focused');
            opt.setAttribute('aria-selected', 'false');
        });

        if (option) {
            option.classList.add('focused');
            option.setAttribute('aria-selected', 'true');
            option.scrollIntoView({ block: 'nearest' });
        }
    }

    getCurrentOption() {
        const dataList = this.shadowRoot.querySelector('svea-datalist');
        if (!dataList) return null;

        return dataList.shadowRoot?.querySelector('.option.focused') || null;
    }

    selectCurrentOption() {
        const currentOption = this.getCurrentOption();
        if (!currentOption) return;

        currentOption.click();

        if (this.settings.localization?.announcementOptionSelected) {
            this.updateLiveRegion(
                this.settings.localization.announcementOptionSelected.replace(
                    '{option}',
                    currentOption.textContent
                )
            );
        } else
            this.updateLiveRegion(
                `Option ${currentOption.textContent} selected`
            );
    }

    isDropdownOpen() {
        return (
            this.dropdownContent && this.dropdownContent.hasAttribute('open')
        );
    }

    isDropdownClosed() {
        return !this.isDropdownOpen();
    }

    openDropdown() {
        const container = this.shadowRoot.querySelector(
            '.multi-select-container'
        );

        const input = this.shadowRoot.querySelector('.multi-select-input');

        if (this.dropdownContent) this.dropdownContent.setAttribute('open', '');

        if (container && !container.classList.contains('open'))
            container.classList.add('open');

        if (input) input.setAttribute('aria-expanded', 'true');
    }

    closeDropdown() {
        const container = this.shadowRoot.querySelector(
            '.multi-select-container'
        );

        const input = this.shadowRoot.querySelector('.multi-select-input');

        if (this.dropdownContent) this.dropdownContent.removeAttribute('open');

        if (container) container.classList.remove('open');

        if (input) input.setAttribute('aria-expanded', 'false');
    }

    removeLastSelectedOption() {
        const selectedOptions = this.selectedOptions;
        if (selectedOptions.length > 0) {
            const lastOption = selectedOptions[selectedOptions.length - 1];
            this.removeSelectedOption(lastOption[this.settings.valueProperty]);

            if (this.settings.localization?.announcementOptionRemoved) {
                this.updateLiveRegion(
                    this.settings.localization.announcementOptionRemoved.replace(
                        '{option}',
                        lastOption.label
                    )
                );
            } else {
                this.updateLiveRegion(`Option ${lastOption.label} removed`);
            }
        }
    }

    updateLiveRegion(message) {
        const liveRegion = this.shadowRoot.querySelector(
            '.multi-select-live-region'
        );

        if (liveRegion) liveRegion.textContent = message;

        setTimeout(() => {
            liveRegion.textContent = '';
        }, 100);
    }

    onSelectionChange(event) {
        if (!event || !event.detail) return;
        const all = event.detail.options || [];

        const selectedOptions = [...this.selectedOptions];
        all.forEach((option) => {
            const alreadySelected = selectedOptions.find(
                (opt) =>
                    opt[this.settings.valueProperty ?? 'value'] ===
                    option[this.settings.valueProperty ?? 'value']
            );

            if (alreadySelected && !option.selected) {
                selectedOptions.splice(
                    selectedOptions.indexOf(alreadySelected),
                    1
                );
            }

            if (!alreadySelected && option.selected) {
                selectedOptions.push({
                    [this.settings.valueProperty ?? 'value']:
                        option[this.settings.valueProperty ?? 'value'],
                    [this.settings.labelProperty ?? 'label']:
                        option[this.settings.labelProperty ?? 'label'],
                });
            }
        });

        this.selectedOptions = selectedOptions;

        this.emit('change', {
            selectedOptions: this.selectedOptions,
        });

        this.handleSelectionChange();
    }

    handleSelectionChange() {
        const input = this.shadowRoot.querySelector('.multi-select-input');
        const inputContainer = this.shadowRoot.querySelector(
            '.multi-select-input-container'
        );

        this.renderSelectedOptions();

        if (this.selectedOptions.length > 0 || (input && input.value)) {
            if (!inputContainer.classList.contains('has-options')) {
                inputContainer.classList.add('has-options');
            }
        } else inputContainer.classList.remove('has-options');

        if (this.settings.localization?.announcementOptionsSelected) {
            this.updateLiveRegion(
                this.settings.localization.announcementOptionsSelected.replace(
                    '{count}',
                    this.selectedOptions.length
                )
            );
        } else
            this.updateLiveRegion(
                `${this.selectedOptions.length} options selected`
            );
    }

    renderSelectedOptions() {
        if (this.shadowRoot === null) return;

        const container = this.shadowRoot.querySelector('.selected-options');
        const inputContainer = this.shadowRoot.querySelector(
            '.multi-select-input-container'
        );
        if (!container) return;

        container.replaceChildren();

        if (this.selectedOptions.length > 0)
            inputContainer.classList.add('has-options');
        else inputContainer.classList.remove('has-options');

        let displayedOptions = this.selectedOptions;
        if (this.settings.maxDisplayed) {
            displayedOptions = this.selectedOptions.slice(
                0,
                this.settings.maxDisplayed
            );
        }

        displayedOptions.forEach((option) => {
            const optionElement = document.createElement('svea-pill');
            optionElement.setAttribute('removable', '');
            if (this.settings.localization?.pillLabelText) {
                optionElement.setAttribute(
                    'aria-label',
                    this.settings.localization.pillLabelText.replace(
                        '{option}',
                        option.label
                    )
                );
            } else {
                optionElement.setAttribute(
                    'aria-label-text',
                    `Remove ${option.label}`
                );
            }
            optionElement.textContent =
                option[this.settings.labelProperty ?? 'label'];
            optionElement.setAttribute('small', '');
            optionElement.setAttribute(
                'data-value',
                option[this.settings.valueProperty]
            );

            optionElement.addEventListener('pill:remove', (e) => {
                e.stopPropagation();
                this.removeSelectedOption(
                    option[this.settings.valueProperty ?? 'value']
                );
            });

            optionElement.addEventListener('click', (e) => {
                e.stopPropagation();
            });

            container.appendChild(optionElement);
        });

        if (
            this.settings.maxDisplayed &&
            this.selectedOptions.length > this.settings.maxDisplayed
        ) {
            const moreCount =
                this.selectedOptions.length - this.settings.maxDisplayed;
            const moreElement = document.createElement('span');
            if (this.settings.localization.andCountMore) {
                moreElement.textContent =
                    this.settings.localization.andCountMore.replace(
                        '{count}',
                        moreCount
                    );
            } else {
                moreElement.textContent = `+${moreCount} more`;
            }
            container.appendChild(moreElement);
        }
    }

    removeSelectedOption(valueToRemove) {
        const dataList = this.shadowRoot.querySelector('svea-datalist');
        if (!dataList) return;

        this.selectedOptions = this.selectedOptions.filter(
            (option) => option[this.settings.valueProperty] !== valueToRemove
        );

        const filteredWithSelectState = this.filteredOptions.map((option) => ({
            ...option,
            selected: this.selectedOptions.some(
                (selected) =>
                    selected[this.settings.valueProperty] ===
                    option[this.settings.valueProperty]
            ),
        }));

        dataList.setOptions(filteredWithSelectState);

        this.emit('change', {
            selectedOptions: this.selectedOptions,
        });

        this.handleSelectionChange();
    }

    clear(silent = false) {
        const dataList = this.shadowRoot.querySelector('svea-datalist');
        if (!dataList) return;

        this.selectedOptions = [];

        dataList.clear(silent);

        this.handleSelectionChange();
    }

    setOptions(options) {
        const dataList = this.shadowRoot.querySelector('svea-datalist');
        if (!dataList) return;

        this.settings.options = options;
        this.filteredOptions = options;

        dataList.setOptions(this.filteredOptions);
    }

    getInteractiveElements() {
        const pills = this.shadowRoot.querySelectorAll('svea-pill');
        const input = this.shadowRoot.querySelector('.multi-select-input');
        const datalist = this.shadowRoot.querySelector('svea-datalist');
        const datalistElement =
            datalist.shadowRoot.querySelector('[tabindex="0"]');

        if (datalistElement) return [...pills, input, datalistElement];

        return [...pills, input];
    }

    focusNextElement() {
        const interactiveElements = this.getInteractiveElements();
        const currentIndex = interactiveElements.findIndex(
            (el) => el === this.shadowRoot.activeElement
        );

        const nextIndex = (currentIndex + 1) % interactiveElements.length;
        const nextElement = interactiveElements[nextIndex];

        if (nextElement) nextElement.focus();
    }

    emit(type, detail = {}) {
        this.dispatchEvent(
            new CustomEvent(`multiselect:${type}`, {
                detail,
                bubbles: true,
                composed: true,
            })
        );
    }
}

customElements.define('svea-multi-select', MultiSelect);
